import GroupMemberRecord from "models/view-models/group-member-record";
import GroupMigrateService from "utilities/services/groups/group-migrate-service";
import GroupMigrationRequestRecord from "models/view-models/group-migration-request-record";
import GroupMigrationStatus from "models/enumerations/group-migration-status";
import InitializeGroupRecord from "models/view-models/group-invitations/initialize-group-record";
import InitializeGroupService from "utilities/services/groups/initialize-group-service";
import UserLoginService from "utilities/services/user-logins/user-login-service";
import UserRoleGroupService from "utilities/services/user-role-groups/user-role-group-service";
import UserRoleService from "utilities/services/users/user-roles/user-role-service";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import useRefreshIdentity from "utilities/hooks/use-refresh-identity";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import { useCallback, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useGroupContext } from "utilities/contexts/group/group-context-provider";
import useTeamOnboardingAlerts from "./use-team-onboarding-notifications";
import { ResultErrorRecord } from "@rsm-hcd/javascript-core";
import { CollectionUtils } from "utilities/collection-utils";

interface UseApplyTeamOnboardingHookOptions {
    teamMembersToInvite: string[];
    teamMembersToMove: GroupMemberRecord[];
    onApplySuccess: () => void;
    onApplyError: (error?: string) => void;
}

const MIGRATING_USER_ERROR_KEY = "Web.GroupsController.ERROR_MIGRATING_USER";
const SENDING_INVITES_ERROR_KEY = "Web.GroupsController.ERROR_SENDING_EMAIL";
const ERROR_MIGRATING_TEAM = "ERROR_MIGRATING_TEAM";
const ERROR_INITIALIZING_TEAM = "ERROR_INITIALIZING_TEAM";
const ERROR_SWITCHING_PROFILE = "ERROR_SWITCHING_PROFILE";

// -----------------------------------------------------------------------------------------
// #region Hook
// -----------------------------------------------------------------------------------------

export function useApplyTeamOnboarding(
    options: UseApplyTeamOnboardingHookOptions
) {
    const {
        teamMembersToInvite,
        teamMembersToMove,
        onApplySuccess,
        onApplyError,
    } = options;

    const { globalState, setGlobalState } = useGlobalState();
    const { currentIdentity } = useCurrentIdentity();
    const { refresh } = useRefreshIdentity();
    const { groupContext } = useGroupContext();
    const {
        showWarningAboutEmailInvitationFailure,
        showSuccessfulInitializationAlert,
        showErroredInitializationAlert,
        showErroredMigrationAlert,
        showWarningAboutMemberMigrationFailure,
    } = useTeamOnboardingAlerts();

    const { list: listUserRoleGroups } = UserRoleGroupService.useList();
    const { list: listUserRoles } = UserRoleService.useList();
    const { update: updateUserLoginApi } = UserLoginService.useUpdate();
    const { update: migrate } = GroupMigrateService.useMigrate();
    const [saving, setSaving] = useState(false);

    const teamName = groupContext?.teamName ?? "";
    const currentGroupId = currentIdentity?.getCurrentTeamId() ?? -1;
    const targetGroupId = groupContext?.groupId ?? -1;

    const applyTeamOnboarding = async () => {
        setSaving(true);
        try {
            if (groupContext.isNewTeam) {
                await initializeGroup();
            } else {
                await migrateGroup();

                console.log("switch!");
                await switchOwnerProfileToMigratedTeam();
            }

            await refresh(true);

            setGlobalState((previous) => previous.clearUnauthorizedResult());

            // I've seen this elsewhere where we switch the account, It seems to be a way to
            // ensuring setting global state is complete prior to performing the action
            // we need to do after the global state change.
            setTimeout(() => {
                setSaving(false);
                onApplySuccess();
            }, 1500);
        } catch (e) {
            onApplyError();
            setSaving(false);
        } finally {
            setSaving(false);
        }
    };

    // Initialize the group with all team member emails
    const initializeGroup = async (): Promise<boolean> => {
        try {
            const initializeGroupRequest = new InitializeGroupRecord({
                name: teamName,
                emails: teamMembersToInvite,
            });

            const response = await InitializeGroupService.initialize(
                initializeGroupRequest,
                { groupId: currentGroupId }
            );

            if (response.status >= 400) {
                throw ERROR_INITIALIZING_TEAM;
            }

            // Handle success but when some invites fail
            if (response.result?.hasErrors()) {
                const erroredEmails = response.result.errors?.map(
                    (error) => error.message!
                );
                showWarningAboutEmailInvitationFailure(erroredEmails!);
            } else {
                showSuccessfulInitializationAlert();
            }

            return true;
        } catch (error) {
            showErroredInitializationAlert();
            throw error;
        }
    };

    const migrateGroup = async () => {
        try {
            const response = await migrate(
                new GroupMigrationRequestRecord({
                    sourceGroupId: currentGroupId,
                    targetGroupId: targetGroupId,
                    teamMembersToMove: teamMembersToMove,
                    teamMemberEmailsToInvite: teamMembersToInvite,
                }),
                { sourceGroupId: currentGroupId }
            );

            // Verify that there was no catostrophic errors, and no non-email errors
            const migrationResponse = response.result?.resultObject;
            if (
                !migrationResponse ||
                migrationResponse.status !== GroupMigrationStatus.Complete
            ) {
                throw ERROR_MIGRATING_TEAM;
            }

            // Check to make sure it also did not fail with any unexpected errors
            if (response.result?.hasErrors()) {
                // Filter out invite errors, member errors, and other errors
                const { inviteErrors, memberErrors, otherErrors } =
                    categorizeErrors(response.result.errors!);

                // Unknown errors, should display as failure
                if (otherErrors) {
                    throw ERROR_MIGRATING_TEAM;
                }

                // Otherwise, show warnings but let them continue on
                if (inviteErrors) {
                    showWarningAboutEmailInvitationFailure(
                        inviteErrors.map((error) => error.message!)
                    );
                }
                if (memberErrors) {
                    showWarningAboutMemberMigrationFailure(
                        memberErrors.map((error) => error.message!)
                    );
                }
            } else {
                showSuccessfulInitializationAlert();
            }
        } catch (e) {
            showErroredMigrationAlert();
            throw e;
        }
    };

    const switchOwnerProfileToMigratedTeam = async () => {
        try {
            const dto = await getUserLoginDtoForMigratedGroup();

            const userLoginResponse = await updateUserLoginApi(dto!);
            if (userLoginResponse.result?.hasErrors()) {
                throw ERROR_SWITCHING_PROFILE;
            }
        } catch {
            ToastManager.error(
                t("errors-actionResource_possessive", {
                    action: t("switching"),
                    resource: t("profile"),
                })
            );
        }
    };

    const getUserLoginDtoForMigratedGroup = useCallback(async () => {
        const userId = currentIdentity?.userId();
        if (userId == null) {
            throw new Error("User ID is null.");
        }

        const listUserRoleGroupsPromise = listUserRoleGroups({
            userId,
            groupId: targetGroupId,
        });
        const listUserRolesPromise = listUserRoles({ userId });

        const [listUserRoleGroupsResponse, listUserRolesResponse] =
            await Promise.all([
                listUserRoleGroupsPromise,
                listUserRolesPromise,
            ]);

        const { resultObjects: userRoleGroups } = listUserRoleGroupsResponse;
        const { resultObjects: userRoles } = listUserRolesResponse;

        const userRoleGroup = userRoleGroups.find(
            (group) => group.groupId === targetGroupId
        );

        const userRole = userRoles.find(
            (ur) => ur.id === userRoleGroup?.userRoleId
        );

        return globalState.currentIdentity?.userLogin?.with({
            roleId: userRole?.roleId,
            userRoleId: userRoleGroup?.userRoleId,
        });
    }, [
        currentIdentity,
        globalState.currentIdentity?.userLogin,
        listUserRoleGroups,
        listUserRoles,
        targetGroupId,
    ]);

    return {
        applyTeamOnboarding,
        saving,
    };
}
// #endregion Hook

const getErrorByKey = (
    errors: ResultErrorRecord[],
    key: string
): ResultErrorRecord[] => errors.filter((error) => error.key === key);

const categorizeErrors = (
    errors: ResultErrorRecord[]
): {
    inviteErrors: ResultErrorRecord[];
    memberErrors: ResultErrorRecord[];
    otherErrors: ResultErrorRecord[];
} => {
    const inviteErrors = getErrorByKey(errors, SENDING_INVITES_ERROR_KEY);
    const memberErrors = getErrorByKey(errors, MIGRATING_USER_ERROR_KEY);
    const otherErrors = CollectionUtils.difference(errors, [
        ...inviteErrors,
        ...memberErrors,
    ]);
    return {
        inviteErrors,
        memberErrors,
        otherErrors,
    };
};
